
import React from 'react';
import { Bot } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTopBots } from '@/hooks/use-top-bots';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

export const TopPerformersCard = () => {
  const { data: topBots, isLoading } = useTopBots();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const getBotTypeBadgeStyle = (type: 'premium_bot' | 'prop_bot') => {
    return type === 'premium_bot' 
      ? 'bg-green-500/10 text-green-500 border-green-500/20'
      : 'bg-purple-500/10 text-purple-500 border-purple-500/20';
  };

  if (isLoading) {
    return (
      <Card className="bg-card p-3 md:p-4 rounded-lg border border-border">
        <div className="flex justify-between items-center mb-2 md:mb-3">
          <h3 className={`${isMobile ? "text-sm" : "text-base"} font-medium`}>{t('top_performers')}</h3>
          <Button variant="link" size="sm" className="text-primary p-0 h-auto text-xs md:text-sm">
            {t('view_all')}
          </Button>
        </div>
        <div className="space-y-2 md:space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <Skeleton className="w-6 h-6 md:w-8 md:h-8 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-3 md:h-4 w-16 md:w-24" />
                  <Skeleton className="h-2 md:h-3 w-12 md:w-16" />
                </div>
              </div>
              <Skeleton className="h-3 md:h-4 w-12 md:w-16" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-card p-3 md:p-4 rounded-lg border border-border" data-tour="bot-performance">
      <div className="flex justify-between items-center mb-2 md:mb-3">
        <h3 className={`${isMobile ? "text-sm" : "text-base"} font-medium`}>{t('top_performers')}</h3>
        <Button variant="link" size="sm" className="text-primary p-0 h-auto text-xs md:text-sm">
          {t('view_all')}
        </Button>
      </div>
      <div className="space-y-2 md:space-y-3">
        {topBots?.map((bot) => (
          <div key={bot.bot_id} className="flex justify-between items-center p-1.5 md:p-2 rounded-md hover:bg-accent transition-colors">
            <div className="flex items-center">
              <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary/20 flex items-center justify-center mr-2 md:mr-3">
                <Bot size={isMobile ? 14 : 16} className="text-primary" />
              </div>
              <div>
                <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>{bot.name}</p>
                <Badge 
                  variant="outline" 
                  className={`${getBotTypeBadgeStyle(bot.type)} text-[10px] md:text-xs px-1.5 md:px-2`}
                >
                  {bot.type === 'premium_bot' ? t('premium') : t('prop')}
                </Badge>
              </div>
            </div>
            <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>
              {bot.connected_accounts_total} {t('accounts')}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
};
